import { forwardRef, Suspense, useImperativeHandle, PropsWithoutRef } from 'react';
import {
    createZodFormResolver,
    FormInputField,
    FormTextareaField,
    lt,
    showSuccessAlert,
    useFormControls,
} from '@mews/framework';
import { Card, Grid, Stack } from '@optimus-web/core';
import { Loader } from '@mews-commander/ui';
import { localizeCurrencyValue } from '@mews-commander/utils';
import { PaymentCardFormat } from '@mews-commander/data-access';
import { PaymentFormProps } from '../types';
import { PaymentAmountSection } from '../../../common/PaymentAmountSection';
import { PaymentCommonSection } from '../../../common/PaymentCommonSection';
import {
    useGetAccountingConfigurationOptionsSuspense,
    useGetPaymentMethodsSuspense,
    useInvalidatePaymentMethodsTableRecordsQuery,
    useInvalidatePreauthorizationTableRecordQuery,
    useRecordCard,
} from '../../../../queries';
import { isNewCard } from '../../../../utils';
import { useDefaultFormContext } from '../useDefaultFormValues';
import { PaymentMethodSelectField } from '../PaymentMethodSelectField/PaymentMethodSelectField';
import { ExternalCardPaymentFormField } from '../ExternalCardPaymentForm/schema';
import { RecordCardFormFields } from '../RecordCardForm/RecordCardFormFields';
import { ProcessPaymentFormField } from '../ProcessPaymentFormSchema';
import { useAddNonGatewayPreauthorization } from '../queries';
import {
    createExternalPreauthorizationForNewCardSchema,
    createExternalPreauthorizationForSelectedCardSchema,
    ExternalPreauthorizationFormField,
    ExternalPreauthorizationFormValues,
    isExternalPreauthorizationForNewCardFormValues,
} from './schema';

type OnlinePaymentFormProps = PaymentFormProps;

export const ExternalPreauthorizationForm = forwardRef(
    ({ accountId, onSuccess, onSubmitting, onSubmitted }: PropsWithoutRef<OnlinePaymentFormProps>, ref) => {
        const invalidatePaymentMethods = useInvalidatePaymentMethodsTableRecordsQuery();
        const invalidatePreauthorizationTableRecordQuery = useInvalidatePreauthorizationTableRecordQuery();

        const {
            data: { paymentCards },
        } = useGetPaymentMethodsSuspense({ accountId });
        const { data: accountingConfigurationOptions } = useGetAccountingConfigurationOptionsSuspense();
        const optionalCreditCardPaymentDetailsEnabled = accountingConfigurationOptions.optionalCreditCardPaymentDetails;

        const { defaultFormValues } = useDefaultFormContext();

        const recordCard = useRecordCard({ accountId });
        const addNonGatewayPreauthorization = useAddNonGatewayPreauthorization({ accountId });

        const { renderFormContent, submit, hasDirtyFields, watch, setValue } =
            useFormControls<ExternalPreauthorizationFormValues>({
                onSubmit: async (formValues) => {
                    try {
                        onSubmitting?.();

                        const { paymentMethodId, amount, currencyCode } = formValues;
                        let creditCardId = paymentMethodId;
                        let cardLastDigits = paymentCards?.find(({ id }) => id === creditCardId)?.lastDigits;

                        if (isExternalPreauthorizationForNewCardFormValues(formValues)) {
                            const { id, lastDigits } = await recordCard({
                                ...formValues,
                                paymentCardFormat: formValues.paymentCardFormat ?? PaymentCardFormat.Physical,
                            });

                            setValue(ExternalCardPaymentFormField.PaymentMethodId, id);
                            creditCardId = id;
                            cardLastDigits = lastDigits;

                            invalidatePaymentMethods({ customerId: accountId, isPreauthorization: true });

                            showSuccessAlert({
                                description: lt('CardCreatedSuccessMessage', { cardLastDigits: lastDigits ?? '' }),
                            });
                        }

                        await addNonGatewayPreauthorization({ ...formValues, paymentMethodId: creditCardId });

                        showSuccessAlert({
                            description: lt('CardPaymentSuccessMessage', {
                                cardLastDigits: cardLastDigits ?? '',
                                amount: localizeCurrencyValue({ value: amount, currency: currencyCode }),
                            }),
                        });

                        invalidatePreauthorizationTableRecordQuery({ accountId });

                        onSuccess?.();
                    } finally {
                        onSubmitted?.();
                    }
                },
                defaultValues: {
                    ...defaultFormValues,
                    [ExternalCardPaymentFormField.PaymentMethodId]: defaultFormValues.paymentMethodId ?? undefined,
                },
                resolver: (values) => {
                    const schema = isNewCard(values[ExternalCardPaymentFormField.PaymentMethodId])
                        ? createExternalPreauthorizationForNewCardSchema(optionalCreditCardPaymentDetailsEnabled)
                        : createExternalPreauthorizationForSelectedCardSchema(optionalCreditCardPaymentDetailsEnabled);

                    return createZodFormResolver(schema)(values);
                },
            });

        useImperativeHandle(
            ref,
            () => ({
                submit,
                hasDirtyFields,
            }),
            [submit, hasDirtyFields]
        );
        return renderFormContent(
            <Stack spacing="300" vertical>
                <PaymentCommonSection hideLinkPaymentToPlan />
                <PaymentMethodSelectField
                    required
                    name={ExternalCardPaymentFormField.PaymentMethodId}
                    label={lt('PaymentCard')}
                    helper={lt('ProcessPaymentMewsPaymentsCardPaymentCardSelectionHint')}
                    accountId={accountId}
                    optionalCreditCardPaymentDetails={accountingConfigurationOptions.optionalCreditCardPaymentDetails}
                    isPreauthorization
                />
                {isNewCard(watch(ExternalCardPaymentFormField.PaymentMethodId)) ? (
                    <Card padding="300" outline>
                        <Suspense fallback={<Loader />}>
                            <RecordCardFormFields />
                        </Suspense>
                    </Card>
                ) : null}
                <FormTextareaField
                    name={ExternalCardPaymentFormField.ReceiptIdentifier}
                    label={lt('ReceiptIdentifier')}
                    helper={lt('ProcessPaymentExternalPaymentsCardPaymentReceiptIdentifierHint')}
                    rows={1}
                />
                <PaymentAmountSection />
                <Grid columnGap="100" templateColumns="1fr 1fr">
                    <FormInputField name={ExternalPreauthorizationFormField.Code} label={lt('Code')} />
                    <FormInputField name={ExternalPreauthorizationFormField.SequenceCode} label={lt('SequenceCode')} />
                </Grid>
                <FormTextareaField name={ProcessPaymentFormField.Notes} label={lt('Notes')} rows={1} />
            </Stack>
        );
    }
);
